import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import InventoryService from '../../services/InventoryService';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
  Chip,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  FilterList as FilterListIcon,
  Clear as ClearIcon,
  Inventory as InventoryIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import axios from 'axios';

const InventoryList = () => {
  const navigate = useNavigate();
  
  const [inventoryItems, setInventoryItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Search and filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredItems, setFilteredItems] = useState([]);
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [stockFilter, setStockFilter] = useState('all');
  const [categories, setCategories] = useState(['all']);
  
  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  // Sorting state
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');
  
  const fetchInventoryItems = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await InventoryService.getAllItems();
      const items = response.data || response;
      
      setInventoryItems(items);
      
      // Extract unique categories for filter dropdown
      const uniqueCategories = ['all', ...new Set(items.map(item => item.categoryName).filter(Boolean))];
      setCategories(uniqueCategories);
      
      setLoading(false);
    } catch (error) {
      console.error('Error fetching inventory items:', error);
      setError('Failed to load inventory items. Please try again later.');
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInventoryItems();
  }, []);
  
  useEffect(() => {
    // Filter inventory items based on search term, category, and stock level
    let filtered = inventoryItems;
    
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(item => 
        (item.itemName || '').toLowerCase().includes(term) ||
        (item.categoryName || '').toLowerCase().includes(term) ||
        (item.supplier || '').toLowerCase().includes(term)
      );
    }
    
    if (categoryFilter !== 'all') {
      filtered = filtered.filter(item => item.categoryName === categoryFilter);
    }
    
    if (stockFilter !== 'all') {
      switch (stockFilter) {
        case 'low':
          filtered = filtered.filter(item => item.CurrentStock <= item.MinimumStock);
          break;
        case 'normal':
          filtered = filtered.filter(item => item.CurrentStock > item.MinimumStock);
          break;
        default:
          break;
      }
    }
    
    // Sort the filtered items
    filtered.sort((a, b) => {
      let aValue, bValue;
      
      // Map frontend sort keys to backend properties
      switch (sortBy) {
        case 'name':
          aValue = a.itemName || '';
          bValue = b.itemName || '';
          break;
        case 'category':
          aValue = a.categoryName || '';
          bValue = b.categoryName || '';
          break;
        case 'quantity':
          aValue = Number(a.currentStock || 0);
          bValue = Number(b.currentStock || 0);
          break;
        case 'reorderLevel':
          aValue = Number(a.minimumStock || 0);
          bValue = Number(b.minimumStock || 0);
          break;
        case 'unit':
          aValue = a.measurementUnit || '';
          bValue = b.measurementUnit || '';
          break;
        case 'lastRestocked':
          aValue = new Date(a.updatedAt || a.createdAt || 0);
          bValue = new Date(b.updatedAt || b.createdAt || 0);
          break;
        default:
          aValue = String(a[sortBy] || '').toLowerCase();
          bValue = String(b[sortBy] || '').toLowerCase();
      }
      
      // Handle different data types
      if (sortBy === 'quantity' || sortBy === 'reorderLevel') {
        // Already handled above
      } else if (sortBy === 'lastRestocked') {
        // Already handled above
      } else {
        aValue = String(aValue).toLowerCase();
        bValue = String(bValue).toLowerCase();
      }
      
      if (sortOrder === 'asc') {
        return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      } else {
        return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
      }
    });
    
    setFilteredItems(filtered);
    setPage(0); // Reset to first page when filtering
  }, [searchTerm, categoryFilter, stockFilter, inventoryItems, sortBy, sortOrder]);
  
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };
  
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  
  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };
  
  const clearSearch = () => {
    setSearchTerm('');
  };
  
  const handleCategoryFilterChange = (event) => {
    setCategoryFilter(event.target.value);
  };
  
  const handleStockFilterChange = (event) => {
    setStockFilter(event.target.value);
  };
  
  const clearFilters = () => {
    setSearchTerm('');
    setCategoryFilter('all');
    setStockFilter('all');
  };
  
  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
  };
  
  const handleViewItem = (itemId) => {
    navigate(`/inventory/${itemId}`);
  };
  
  const handleEditItem = (itemId) => {
    navigate(`/inventory/${itemId}/edit`);
  };
  
  const handleDeleteItem = async (itemId) => {
    if (window.confirm('Are you sure you want to delete this inventory item?')) {
      try {
        await InventoryService.deleteItem(itemId);
        
        // Refresh the inventory list after successful deletion
        fetchInventoryItems();
      } catch (error) {
        console.error('Error deleting inventory item:', error);
        alert('Failed to delete inventory item. Please try again.');
      }
    }
  };
  
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <CircularProgress />
      </Box>
    );
  }
  
  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error" variant="h6">{error}</Typography>
        <Button variant="contained" sx={{ mt: 2 }} onClick={() => window.location.reload()}>
          Retry
        </Button>
      </Box>
    );
  }
  
  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Inventory Management
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<InventoryIcon />}
            onClick={() => navigate('/inventory/stocktakes')}
          >
            Manage Stocktakes
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => navigate('/inventory/new')}
          >
            Add Item
          </Button>
        </Box>
      </Box>
      
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Search"
                value={searchTerm}
                onChange={handleSearchChange}
                placeholder="Search by name, category, supplier..."
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                  endAdornment: searchTerm && (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={clearSearch}>
                        <ClearIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel id="category-filter-label">Category</InputLabel>
                <Select
                  labelId="category-filter-label"
                  value={categoryFilter}
                  label="Category"
                  onChange={handleCategoryFilterChange}
                >
                  {categories.map((category) => (
                    <MenuItem key={category} value={category}>
                      {category === 'all' ? 'All Categories' : category}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel id="stock-filter-label">Stock Level</InputLabel>
                <Select
                  labelId="stock-filter-label"
                  value={stockFilter}
                  label="Stock Level"
                  onChange={handleStockFilterChange}
                >
                  <MenuItem value="all">All Levels</MenuItem>
                  <MenuItem value="low">Low Stock</MenuItem>
                  <MenuItem value="normal">Normal Stock</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={2}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<ClearIcon />}
                onClick={clearFilters}
                disabled={!searchTerm && categoryFilter === 'all' && stockFilter === 'all'}
              >
                Clear Filters
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
      
      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        <TableContainer sx={{ maxHeight: 600 }}>
          <Table stickyHeader aria-label="inventory items table">
            <TableHead>
              <TableRow>
                <TableCell>
                  <TableSortLabel
                    active={sortBy === 'name'}
                    direction={sortBy === 'name' ? sortOrder : 'asc'}
                    onClick={() => handleSort('name')}
                  >
                    Name
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={sortBy === 'category'}
                    direction={sortBy === 'category' ? sortOrder : 'asc'}
                    onClick={() => handleSort('category')}
                  >
                    Category
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">
                  <TableSortLabel
                    active={sortBy === 'quantity'}
                    direction={sortBy === 'quantity' ? sortOrder : 'asc'}
                    onClick={() => handleSort('quantity')}
                  >
                    Quantity
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">
                  <TableSortLabel
                    active={sortBy === 'reorderLevel'}
                    direction={sortBy === 'reorderLevel' ? sortOrder : 'asc'}
                    onClick={() => handleSort('reorderLevel')}
                  >
                    Reorder Level
                  </TableSortLabel>
                </TableCell>
                <TableCell>Unit</TableCell>
                <TableCell>
                  <TableSortLabel
                    active={sortBy === 'supplier'}
                    direction={sortBy === 'supplier' ? sortOrder : 'asc'}
                    onClick={() => handleSort('supplier')}
                  >
                    Supplier
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={sortBy === 'lastRestocked'}
                    direction={sortBy === 'lastRestocked' ? sortOrder : 'asc'}
                    onClick={() => handleSort('lastRestocked')}
                  >
                    Last Restocked
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredItems.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Typography variant="body1" sx={{ py: 2 }}>
                      No inventory items found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredItems
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((item) => (
                    <TableRow key={item.itemId} hover>
                      <TableCell>{item.itemName}</TableCell>
                      <TableCell>
                        <Chip label={item.categoryName || 'N/A'} size="small" />
                      </TableCell>
                      <TableCell align="right">
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                          {item.currentStock <= item.minimumStock && (
                            <Tooltip title="Low stock">
                              <WarningIcon color="warning" fontSize="small" sx={{ mr: 1 }} />
                            </Tooltip>
                          )}
                          {item.currentStock}
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                          {item.currentStock <= item.minimumStock && (
                            <Tooltip title="Reorder needed">
                              <WarningIcon color="error" fontSize="small" sx={{ mr: 1 }} />
                            </Tooltip>
                          )}
                          {item.minimumStock}
                        </Box>
                      </TableCell>
                      <TableCell>{item.measurementUnit}</TableCell>
                      <TableCell>{item.supplier || 'N/A'}</TableCell>
                      <TableCell>
                        {item.updatedAt ? new Date(item.updatedAt).toLocaleDateString() : 
                         item.createdAt ? new Date(item.createdAt).toLocaleDateString() : 'N/A'}
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="View">
                          <IconButton
                            size="small"
                            onClick={() => handleViewItem(item.itemId)}
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit">
                          <IconButton
                            size="small"
                            onClick={() => handleEditItem(item.itemId)}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDeleteItem(item.itemId)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={filteredItems.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>
    </Box>
  );
};

export default InventoryList;